import { makeAutoObservable } from 'mobx';

import { WidgetConfigurationProps } from 'controls/designer/features/widgets/widgetConfig';
import { TimePeriod } from 'controls/react/form/timePeriodSelector';
import { MobxManager } from 'framework/mobx-integration';
import { ArrayHelpers } from 'tools/helpers';
import { convertDataSelectorTypeToTimeSelectorType } from 'controls/designer/features/widgets/datasources/datasourceStores';
import { ColorDescriptionType } from 'components/HighchartsWrapper';

import { DataSourceSingleGraphDataSourceConfig, DataSourceSingleGraphWidgetConfig } from '../types';
import { DataSourceSingleGraphRowStore } from './DataSourceSingleGraphRowStore';

function emptyRow(): DataSourceSingleGraphDataSourceConfig {
	return {
		dataSourceId: undefined,
		datasourceName: undefined,
		timeColumn: undefined,
		metricColumn: undefined,
		metricRowName: undefined,
		rowKey: undefined,
		rowKeyValue: undefined,
		chartType: 'line',
		colorSettings: {
			type: ColorDescriptionType.Fixed,
			startColor: '#3388ff',
			finishColor: '#3388ff',
		},
	};
}

export class DataSourceSingleGraphEditorStore {
	rows: DataSourceSingleGraphRowStore[] = [];
	enableTimeSelector: boolean;
	defaultTimeSelector: TimePeriod;
	mobx = new MobxManager();

	get config() {
		return this.props.config;
	}

	get currentDataSource() {
		return this.rows[0]?.currentDataSource;
	}

	constructor(public props: WidgetConfigurationProps<DataSourceSingleGraphWidgetConfig>) {
		makeAutoObservable(this);

		if (!this.config.dataSources?.length) {
			this.config.dataSources = [emptyRow()];
		}
		this.rows = this.config.dataSources.map((row) => this.createRow(row));

		this.mobx.reaction(
			() => this.currentDataSource,
			(dataSource) => {
				this.defaultTimeSelector = convertDataSelectorTypeToTimeSelectorType(dataSource?.dataSelector);
			},
		);

		this.mobx.reaction(
			() => this.enableTimeSelector,
			() => {
				if (this.enableTimeSelector) {
					this.config.period ??= { ...this.defaultTimeSelector };
				} else {
					this.config.period = undefined;
				}
			},
		);
	}

	createRow = (row: DataSourceSingleGraphDataSourceConfig) => {
		return new DataSourceSingleGraphRowStore({ root: this.config, row });
	};

	addRow = () => {
		this.config.dataSources.push(emptyRow());
		this.rows.push(this.createRow(this.config.dataSources[this.config.dataSources.length - 1]));
	};

	removeRow = (rowStore: DataSourceSingleGraphRowStore) => {
		if (this.rows.length <= 1) return;

		ArrayHelpers.remove(this.config.dataSources, rowStore.row);
		ArrayHelpers.remove(this.rows, rowStore);
		rowStore.destroy();
	};

	init = () => {
		this.enableTimeSelector = !!this.config.period;
	};

	destroy() {
		this.mobx.destroy();
		this.rows.forEach((x) => x.destroy());
	}
}
